import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ModifyProfile.css';
import AppHeader from '../components/AppHeader';
import { useToast } from '../components/Toast';
import { apiFetch } from '../utils/apiFetch';
import { FONT_COLOR_PALETTE, DEFAULT_FONT_COLOR } from '../utils/fontColors';

const Profile = () => {
    const toast = useToast();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [formData, setFormData] = useState({
        nickname: '',
        availableEmail: '',  // notificationEmail → availableEmail로 변경
        fontColor: DEFAULT_FONT_COLOR
    });
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
            navigate('/login');
            return;
        }
        
        try {
            const parsedUser = JSON.parse(userData);
            setUser(parsedUser);
            setFormData({
                nickname: parsedUser.nickname || '',
                availableEmail: parsedUser.availableEmail || parsedUser.email || '',  // availableEmail 사용
                fontColor: parsedUser.fontColor || DEFAULT_FONT_COLOR
            });
        } catch (error) {
            console.error('사용자 정보 파싱 오류:', error);
            navigate('/login');
            return;
        }
        
        setIsLoading(false);
    }, [navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 입력 시 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // 닉네임 검증
        if (!formData.nickname.trim()) {
            newErrors.nickname = '닉네임을 입력해주세요.';
        } else if (formData.nickname.length < 2) {
            newErrors.nickname = '닉네임은 2자 이상이어야 합니다.';
        } else if (formData.nickname.length > 8) {
            newErrors.nickname = '닉네임은 8자 이하여야 합니다.';
        }
        
        // 알림 이메일 검증
        if (!formData.availableEmail.trim()) {
            newErrors.availableEmail = '알림 이메일을 입력해주세요.';
        } else if (!isValidEmail(formData.availableEmail)) {
            newErrors.availableEmail = '올바른 이메일 형식을 입력해주세요.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleColorSelect = (hex) => {
        setFormData(prev => ({ ...prev, fontColor: hex }));
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsUpdateLoading(true);

        try {
            const response = await apiFetch('/members/modify', {
                method: 'PUT',
                body: JSON.stringify({
                    nickname: formData.nickname,
                    availableEmail: formData.availableEmail,  // availableEmail로 전송
                    fontColor: formData.fontColor
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                // 로컬 스토리지의 사용자 정보 업데이트
                const updatedUser = {
                    ...user,
                    nickname: result.data.nickname,
                    availableEmail: result.data.availableEmail,  // availableEmail로 업데이트
                    fontColor: result.data.fontColor
                };
                
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                toast.success('프로필이 성공적으로 업데이트되었습니다!');
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '프로필 업데이트에 실패했습니다.');
            }
        } catch (error) {
            console.error('프로필 업데이트 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsUpdateLoading(false);
        }
    };

    const handleResetEmail = () => {
        setFormData(prev => ({
            ...prev,
            availableEmail: user.email  // 카카오 이메일로 초기화
        }));
        if (errors.availableEmail) {
            setErrors(prev => ({
                ...prev,
                availableEmail: ''
            }));
        }
    };

    const handleBack = () => {
        navigate('/home');
    };

    if (isLoading) {
        return (
            <div className="profile-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <div className="profile-container">
            <AppHeader />

            <main className="profile-main">
                <div className="profile-content">
                    <section className="profile-section">
                        <div className="profile-card">
                            <h2 className="profile-title">
                                <div className="title-icon">👤</div>
                                회원정보 수정
                            </h2>
                            

                            {/* 프로필 이미지 표시 (수정 불가) */}
                            <div className="profile-image-display">
                                <div className="profile-image-container">
                                    {user.profileImage ? (
                                        <img 
                                            src={user.profileImage} 
                                            alt="프로필 이미지" 
                                            className="profile-image"
                                        />
                                    ) : (
                                        <div className="default-avatar">👤</div>
                                    )}
                                </div>
                                <p className="profile-image-hint">카카오 계정의 프로필 이미지입니다.</p>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="profile-form">
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">
                                        카카오 이메일
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={user?.email || ''}
                                        disabled
                                        className="form-input disabled"
                                    />
                                    <small className="form-hint">
                                        카카오 계정 이메일입니다. 변경할 수 없습니다.
                                    </small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="nickname" className="form-label">
                                        닉네임 *
                                    </label>
                                    <input
                                        type="text"
                                        id="nickname"
                                        name="nickname"
                                        value={formData.nickname}
                                        onChange={handleInputChange}
                                        className={`form-input ${errors.nickname ? 'error' : ''}`}
                                        placeholder="닉네임을 입력하세요"
                                        maxLength={8}
                                    />
                                    {errors.nickname && (
                                        <span className="error-text">{errors.nickname}</span>
                                    )}
                                    <small className="form-hint">
                                        2-8자 사이로 입력해주세요.
                                    </small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="availableEmail" className="form-label">
                                        알림 이메일 *
                                    </label>
                                    <div className="email-input-group">
                                        <input
                                            type="email"
                                            id="availableEmail"
                                            name="availableEmail"
                                            value={formData.availableEmail}
                                            onChange={handleInputChange}
                                            className={`form-input ${errors.availableEmail ? 'error' : ''}`}
                                            placeholder="알림을 받을 이메일을 입력하세요"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleResetEmail}
                                            className="reset-email-btn"
                                            title="카카오 이메일로 초기화"
                                        >
                                            🔄
                                        </button>
                                    </div>
                                    {errors.availableEmail && (
                                        <span className="error-text">{errors.availableEmail}</span>
                                    )}
                                    <small className="form-hint">
                                        일기 알림, 기념일 알림을 받을 이메일 주소입니다.
                                        {formData.availableEmail === user?.email && (
                                            <span className="default-email-indicator"> (기본값: 카카오 이메일)</span>
                                        )}
                                    </small>
                                </div>
                                
                                <div className="form-group">
                                    <label className="form-label">내 글자 색</label>
                                    <div className="font-color-palette">
                                        {FONT_COLOR_PALETTE.map(color => (
                                            <button
                                                key={color.hex}
                                                type="button"
                                                className={`color-swatch ${formData.fontColor === color.hex ? 'selected' : ''}`}
                                                style={{ backgroundColor: color.hex }}
                                                title={color.name}
                                                aria-label={color.name}
                                                onClick={() => handleColorSelect(color.hex)}
                                            />
                                        ))}
                                    </div>
                                    <small className="form-hint">
                                        일기에서 내가 쓴 부분이 이 색으로 표시됩니다.
                                    </small>
                                </div>

                                <div className="form-preview">
                                    <h3>프로필 미리보기</h3>
                                    <div className="preview-card">
                                        <div className="preview-avatar">
                                            {user.profileImage ? (
                                                <img 
                                                    src={user.profileImage} 
                                                    alt="프로필 미리보기"
                                                    className="preview-image"
                                                />
                                            ) : (
                                                <div className="preview-fallback">👤</div>
                                            )}
                                        </div>
                                        <div className="preview-info">
                                            <p className="preview-nickname" style={{ color: formData.fontColor }}>
                                                {formData.nickname || '닉네임'}
                                            </p>
                                            <p className="preview-email">
                                                {user?.email}
                                            </p>
                                            <p className="preview-notification-email">
                                                📧 알림: {formData.availableEmail || '알림 이메일'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="form-actions">
                                    <button 
                                        type="button" 
                                        className="cancel-btn"
                                        onClick={handleBack}
                                    >
                                        취소
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="submit-btn"
                                        disabled={isUpdateLoading}
                                    >
                                        {isUpdateLoading ? (
                                            <>
                                                <div className="button-spinner"></div>
                                                업데이트 중...
                                            </>
                                        ) : (
                                            '프로필 업데이트'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default Profile;
